<template>
  <div class="edit-additional-questions-card popup-full relative">
    <div class="popup-full__wrap">
      <BasePreloader v-if="loading.question" class="absolute" />

      <div
        v-else
        class="popup-full__content"
      >
        <BasePreloader v-if="loading.save" class="absolute" />

        <h1 class="edit-additional-questions-card__title mb-0">{{ question.question_name }}</h1>

        <div class="find-vacancy-q-page__step">
          <div
            v-if="question.question_type === 'QUESTION_CLOSED'"
            class="find-vacancy-q-page__list"
          >
            <BaseRadioList
              v-model="result"
              :key="question.id"
              :options="question.variants"
              text-key="value"
            >
              <template v-slot:mark>
                <BaseSvg name="ico-check"/>
              </template>
            </BaseRadioList>
          </div>

          <div v-if="question.question_type === 'QUESTION_OPEN'">
            <BaseInput
              v-model="result"
              textarea
            />
          </div>

          <div v-if="currentStepWithFilesUploader" class="mb-3">
            <EditFilesDraggable
              :value="result"
              :max="1"
              :max-caption-length="46"
              :edit-caption-rows-count="3"
              class="additional-questions-responses__edit-files"
              @input="uploadFiles"
              @update="updateFiles"
              @delete="deleteFile"
            />
          </div>
        </div>

        <BaseAlert
          :value="saveError"
          class="find-vacancy-q-page__error  _warning"
        />

        <div class="d-flex justify-flex-end">
          <button
            class="base-btn base-btn--green"
            @click="save"
          >
            {{ $tResumePage('save_answer') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { http } from '../axios';
import handleError from '../helpers/handleError';
import EditFilesDraggable from './vacancyEdit/EditFilesDraggable';
import { v4 } from 'uuid';
import {cloneDeep} from 'lodash';
import {serialize} from 'object-to-formdata';

export default {
  name: 'EditAdditionalQuestionAnswerCard',
  components: {
    EditFilesDraggable,
  },
  props: {
    resumeId: {
      type: [String, Number],
      default: null,
    },
    questionId: {
      type: [String, Number],
      default: null,
    },
    modalName: {
      type: String,
      default: '',
    },
    onUpdate: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      loading: {
        question: false,
        save: false,
      },
      question: null,
      saveError: null,
      result: null,
    };
  },
  computed: {
    currentStepWithFilesUploader() {
      const availableForTypes = ['QUESTION_OPEN_WITH_FILE', 'UPLOAD_FIRST_PASSPORT', 'UPLOAD_ANOTHER_PASSPORT'];
      return availableForTypes.includes(this.question.question_type);
    },
    sendSaveRequest() {
      switch (true) {
        case this.question.question_type === 'QUESTION_OPEN': return this.result !== this.question.answer;
        case this.question.question_type === 'QUESTION_CLOSED': return this.question.variants.find(v => v.selected).id !== this.result.id;
        default: return true;
      }
    },
  },
  created() {
    this.load();
  },
  methods: {
    close() {
      this.$vModal.close(this.modalName);
    },
    async load() {
      this.loading.question = true;

      try {
        const { data } = await http.get('/api/edit-resume/get-question', {
          params: {
            resume_id: this.resumeId,
            question_id: this.questionId,
          },
        });

        this.question = data.data;
        this.result = this.defineCurrentResultValue(data.data);
      } catch (e) {
        this.close();
        this.$vModal.open('warning', { title: 'Something went wrong on edit answer' });
      } finally {
        this.loading.question = false;
      }
    },
    defineCurrentResultValue(data) {
      switch (true) {
        case data.question_type === 'QUESTION_OPEN': return data.answer;
        case data.question_type === 'QUESTION_CLOSED': return data.variants.find(v => v.selected);
        case this.currentStepWithFilesUploader: return this.formFileDataObjects(data.answer);
        default: return data.answer;
      }
    },
    async save() {
      if (!this.sendSaveRequest) return this.close();

      this.loading.save = true;

      try {
        const saveData = this.formSaveData();
        await http.post('/api/edit-resume/save-question', saveData);
        this.onUpdate(this.getUpdatedValue());
        this.close();
      } catch (e) {
        this.saveError = handleError(e);
      } finally {
        this.loading.save = false;
      }
    },
    formSaveData() {
      const tempResult = cloneDeep(this.result);
      const value = Array.isArray(tempResult) ? tempResult.map(this.fileDataObjectToFile) : tempResult;

      const result = {
        resume_id: this.resumeId,
        question_id: this.question.id,
        question_type: this.question.question_type,
        value,
      };

      return serialize(result, { indices: true });
    },
    getUpdatedValue() {
      return this.question.question_type === 'QUESTION_CLOSED' ? this.result.value : this.result;
    },

    uploadFiles(files) {
      const fileData = this.fileToDataObject(files[0], this.result);
      this.result.push(fileData);
    },
    updateFiles(files) {
      this.result = files;
    },
    deleteFile(idx) {
      const file = this.result[idx]?.file;
      if (file) {
        URL.revokeObjectURL(file);
      }
      this.result.splice(idx, 1);
    },

    formFileDataObjects(titleWithUrlObjectsList) {
      const result = [];

      titleWithUrlObjectsList.forEach((fileObj) => {
        const fileDataObject = {
          id: v4(),
          link: fileObj.path,
          ext: fileObj.title.split('.').pop(),
          position: result.length,
          ...fileObj,
        };

        result.push(fileDataObject);
      });

      return result;
    },
    fileToDataObject(file, bindedList) {
      const link = URL.createObjectURL(file);

      return {
        id: v4(),
        link,
        path: link,
        title: file.name,
        ext: file.name.split('.').pop(),
        position: bindedList.length,
        file,
      };
    },
    fileDataObjectToFile(dataObject) {
      const isFileDataObject = dataObject?.file instanceof Blob || dataObject?.path;
      if (isFileDataObject) return { title: dataObject.title, value: dataObject.file || dataObject.path };
      return dataObject;
    },
  },
};
</script>
